import { useEffect, useRef, useState, type ReactNode } from "react";
import { SlidersHorizontal } from "lucide-react";
import { cn } from "@/design-system/lib/utils";

export interface FilterSlot {
  /** Key used for React reconciliation + overflow tracking. */
  key: string;
  label?: string;
  control: ReactNode;
}

interface FilterToolbarProps {
  /** Search input (typically controlled by the host). Rendered first, leftmost. */
  searchInput?: ReactNode;
  /** Filter chips / dropdowns. Overflow into a "More filters" disclosure when width is tight. */
  filters?: FilterSlot[];
  /** Sort dropdown — rendered right-aligned. */
  sort?: ReactNode;
  /** Right-aligned action buttons (e.g. Export). */
  actions?: ReactNode;
  /** When true, the search input grows to fill the available width instead of
   *  sitting at its fixed intrinsic width. */
  growSearch?: boolean;
  className?: string;
}

/**
 * Composable filter bar matching the chrome of {@link PageHeader}. Intended
 * to sit directly below the page header and above a table. Pure layout —
 * the host owns all state.
 */
export function FilterToolbar({
  searchInput,
  filters,
  sort,
  actions,
  growSearch,
  className,
}: FilterToolbarProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const filtersRef = useRef<HTMLDivElement>(null);
  const [overflowCount, setOverflowCount] = useState(0);
  const [overflowOpen, setOverflowOpen] = useState(false);

  // Track how many filters need to hide into the "More filters" disclosure
  // by measuring whether the rendered chips would overflow their container.
  useEffect(() => {
    if (!filters || filters.length === 0) {
      setOverflowCount(0);
      return;
    }
    const container = filtersRef.current;
    if (!container) return;

    const measure = () => {
      const children = Array.from(container.children) as HTMLElement[];
      let total = 0;
      const max = container.clientWidth;
      let hidden = 0;
      for (let i = 0; i < children.length; i++) {
        children[i].style.display = "";
        total += children[i].offsetWidth + 8;
        if (total > max) {
          for (let j = i; j < children.length; j++) {
            children[j].style.display = "none";
          }
          hidden = children.length - i;
          break;
        }
      }
      setOverflowCount(hidden);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => ro.disconnect();
  }, [filters]);

  const overflowItems = filters?.slice(filters.length - overflowCount) ?? [];

  return (
    <div
      ref={containerRef}
      data-testid="filter-toolbar"
      className={cn(
        "flex flex-wrap items-center gap-space-3 mb-space-4 p-space-3",
        "bg-white-pure border border-border-light rounded-lg shadow-sm",
        className,
      )}
    >
      {searchInput && (
        <div className={cn("min-w-0", growSearch ? "flex-1" : "flex-shrink-0")}>
          {searchInput}
        </div>
      )}
      {filters && filters.length > 0 && (
        <div
          ref={filtersRef}
          className="flex flex-1 items-center gap-space-2 overflow-hidden"
        >
          {filters.map((f) => (
            <div
              key={f.key}
              data-filter-key={f.key}
              className="flex items-center gap-space-2"
            >
              {f.label && (
                <span className="font-display text-label uppercase tracking-label text-gold-shadow">
                  {f.label}
                </span>
              )}
              {f.control}
            </div>
          ))}
        </div>
      )}
      {overflowCount > 0 && (
        <div className="relative">
          <button
            type="button"
            onClick={() => setOverflowOpen((v) => !v)}
            className="inline-flex items-center gap-2 px-space-3 h-10 rounded-md border border-border-light text-small text-onyx hover:bg-white-warm"
            aria-expanded={overflowOpen}
            data-testid="filter-toolbar-more"
          >
            <SlidersHorizontal size={14} strokeWidth={1.5} aria-hidden />
            More filters ({overflowCount})
          </button>
          {overflowOpen && (
            <div className="absolute right-0 z-10 mt-1 w-64 rounded-md border border-border-light bg-white-pure shadow-lg p-space-3 flex flex-col gap-space-3">
              {overflowItems.map((f) => (
                <div key={f.key} className="flex flex-col gap-1">
                  {f.label && (
                    <span className="font-display text-label uppercase tracking-label text-gold-shadow">
                      {f.label}
                    </span>
                  )}
                  {f.control}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {sort && <div className="ml-auto flex items-center gap-space-2">{sort}</div>}
      {actions && (
        <div className={cn("flex items-center gap-space-2", !sort && "ml-auto")}>{actions}</div>
      )}
    </div>
  );
}
